import Select from '@mui/material/Select'
import { styled } from '@mui/material/styles'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useRef } from 'react'

import { CHILDREN } from 'constants/props'
import { COLORS } from 'constants/colors'
import Icon from 'generics/Icon'

const notDisplayed = {
  transform: 'scaleX(0)',
  display: 'none',
}

// WCAG 2.4.7 Focus Visible: MUI hides the underline. Instead of a hard "ring", use a Material-style
// state layer (soft rounded fill) — same family as MUI ButtonBase overlay / MD3 focus surfaces.
const StyledSelectInput = styled(Select)({
  '&::before': notDisplayed,
  '&::after': notDisplayed,
  '&:hover:not(.Mui-disabled)::before': notDisplayed,
  '&.Mui-focused::after': notDisplayed,
  '& .MuiSelect-select': {
    backgroundColor: 'transparent',
    borderRadius: 4,
    paddingRight: '32px !important',
    transition: 'background-color 120ms ease, border-radius 120ms ease',
  },
  '& .MuiSelect-select:focus-visible': {
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
    borderRadius: 8,
    boxShadow: 'none',
  },
  '& .MuiListItem-root.Mui-selected': {
    color: COLORS.primaryBlue.rgba,
    background: 'transparent',
  },
  // Make the icon participate in clicks — it's inside the select trigger
  '& .MuiSelect-icon': {
    pointerEvents: 'auto',
    cursor: 'pointer',
  },
})

const AccountSelect = ({ ariaLabel, className, children, iconClassName, ...rest }) => {
  const selectRef = useRef(null)

  return (
    <StyledSelectInput
      ref={selectRef}
      className={classnames(className)}
      fullWidth
      IconComponent={props => (
        <Icon.Stroke7
          {...props}
          name="angle-down"
          className={classnames('MuiSelect-icon', iconClassName)}
          onMouseDown={e => {
            e.stopPropagation()
            // The icon is a sibling of the combobox div inside the MUI root.
            // Find the combobox div (the actual .MuiSelect-select trigger) and
            // fire a real mousedown on it so MUI's internal handler opens the menu.
            const root = selectRef.current
            const combobox = root && root.querySelector('[role="combobox"]')
            if (combobox) {
              combobox.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, button: 0 }))
            }
          }}
        />
      )}
      SelectDisplayProps={{
        'aria-label': ariaLabel,
        'aria-labelledby': null,
      }}
      {...rest}
    >
      {children}
    </StyledSelectInput>
  )
}

AccountSelect.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  children: CHILDREN,
  className: PropTypes.string,
  iconClassName: PropTypes.string,
}

AccountSelect.defaultProps = {
  children: null,
  className: null,
  iconClassName: null,
}

export default AccountSelect
